import { DateTime } from "luxon"

const { humanize } = require("./inflector")
const {
  isNotAValue,
  isNotANumber,
  isDate,
  daysFromToday,
  formatToLocalDateTime,
} = require("./helpers")

function withSuffix(numAsString, suffix) {
  return numAsString.endsWith(")")
    ? numAsString.replace(")", `${suffix})`)
    : numAsString + suffix
}

function abbreviateNumber(num) {
  if (isNotANumber(num)) return { num, suffix: "" }

  const x = Math.abs(num)

  if (x < 1000 || x > 999_999_999_999_999) {
    return { num: x, suffix: "" }
  }

  const suffix = { 3: "K", 6: "M", 9: "B", 12: "T" }

  for (const exp of [12, 9, 6, 3]) {
    const scale = Math.pow(10, exp)
    if (x >= scale) {
      return {
        num: Number(Number.parseFloat((num / scale).toFixed(2))),
        suffix: suffix[exp],
      }
    }
  }
}

function formatAsAbbreviatedNumber({ value: x }) {
  return formatAsBigNumber({ value: x }, { abbreviate: true })
}

function formatAsAbbreviatedMoney({ value: x, unit, options }) {
  return formatAsMoney({ value: x, unit }, { ...options, abbreviate: true })
}

function formatAsAbbreviatedWithUnit({ value: x, unit }, options) {
  return formatWithUnit({ value: x, unit }, { ...options, abbreviate: true })
}

function formatAsDecimalWithUnit({ value: x, unit }, options) {
  return formatWithUnit({ value: x, unit }, { ...options, decimal: true })
}

function formatAsDecimalsWithUnit({ value: x, unit }, options) {
  return formatWithUnit({ value: x, unit }, { ...options, decimals: true })
}

export function formatAsBigNumber(
  { value: x },
  options = { abbreviate: false }
) {
  if (isNotANumber(x)) return x

  const { num, suffix } = options.abbreviate
    ? abbreviateNumber(x)
    : { num: Number(Number.parseFloat(x).toFixed(0)), suffix: "" }

  const parts = num.toString().split(".")
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",")

  return withSuffix(parts.join("."), suffix)
}

function formatAsWholeNumber({ value: x }) {
  if (isNotANumber(x)) return x

  return Number(Number.parseFloat(x).toFixed(0))
}

function formatAsWholeNumberWithUnit({ value: x, unit }, options) {
  return formatWithUnit({ value: x, unit }, { ...options, whole: true })
}

function formatAsNumberDecimal({ value: x }) {
  if (isNotANumber(x)) return x

  return Number(Number.parseFloat(x)).toFixed(1)
}

function formatAsNumberDecimals({ value: x }) {
  if (isNotANumber(x)) return x

  return Number(Number.parseFloat(x)).toFixed(2)
}

function formatAsBigMoney({ value: x }, options = { abbreviate: false }) {
  return "$" + formatAsBigNumber({ value: x }, options)
}

export function formatAsNumberInWords(
  { value: x },
  options = { capitalize: false }
) {
  if (isNotANumber(x)) return x

  if (x < 11) {
    const text = [
      "no",
      "one",
      "two",
      "three",
      "four",
      "five",
      "six",
      "seven",
      "eight",
      "nine",
      "ten",
    ][x]
    return options.capitalize
      ? text.replace(/^\w/, (c) => c.toUpperCase())
      : text
  }
  return x
}

function formatAsPercentage({ value: x }) {
  if (isNotANumber(x)) return x

  return Number(x).toLocaleString("en", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatWithUnit(
  { value: x, unit },
  options = { abbreviate: false, decimal: false, decimals: false, whole: false }
) {
  if (isNotANumber(x)) return x

  const { num, suffix } = options.abbreviate
    ? abbreviateNumber(x)
    : { num: Number(x), suffix: "" }

  const isDecimal = options.decimal
  const isDecimals = options.decimals
  const isWhole = options.whole
  const hasFractions = num - Math.floor(num) !== 0
  const isSmall = Math.abs(num) < 1000
  const showFraction = (isSmall || options.abbreviate) && hasFractions
  const showUnit = "showUnit" in options ? options.showUnit : true

  const numToDisplay = isDecimal
    ? num.toFixed(1)
    : isDecimals
      ? num.toFixed(2)
      : isWhole
        ? num.toFixed(0)
        : isSmall
          ? Number(num.toFixed(showFraction ? 3 : 0)).toString()
          : formatAsBigNumber({ value: num })

  if (unit === undefined || unit === null) {
    return numToDisplay
  }

  return `${withSuffix(numToDisplay, suffix)}${showUnit ? ` ${unit}` : ""}`
}

const SIZE_UNIT_LABELS = { UNITS: "Units", BEDS: "Beds", KEYS: "Keys" }

function formatAsSize({ value: x, unit }, options) {
  const value = isNotANumber(x) ? x : Math.round(Number(x))
  return formatWithUnit(
    { value, unit: SIZE_UNIT_LABELS[unit] || unit },
    options
  )
}

function stripDollarCountryCodePrefix(formattedCurrency) {
  if (formattedCurrency.includes("$")) {
    return formattedCurrency.replace(/[A-Z]+\$/, "$")
  }
  return formattedCurrency
}

export function getCurrencySymbol(currencyCode) {
  const formatter = new Intl.NumberFormat("en", {
    style: "currency",
    currency: currencyCode,
  })

  const parts = formatter.formatToParts(1)
  const symbolPart = parts.find((part) => part.type === "currency")

  if (!symbolPart) return null
  return stripDollarCountryCodePrefix(symbolPart.value)
}

function formatAsMoney({ value: x, unit }, options = { abbreviate: false }) {
  if (isNotANumber(x) || isNotAValue(unit)) return x

  const { num, suffix } = options.abbreviate
    ? abbreviateNumber(x)
    : { num: Number(x), suffix: "" }

  const hasFractions = num - Math.floor(num) !== 0
  const isSmall = Math.abs(num) < 1000
  const showFraction = (isSmall || options.abbreviate) && hasFractions

  const currencyFormatter = new Intl.NumberFormat("en", {
    style: "currency",
    currencySign: "accounting",
    currency: unit,
    minimumFractionDigits: showFraction ? 2 : 0,
    maximumFractionDigits: showFraction ? 2 : 0,
  })

  return stripDollarCountryCodePrefix(
    withSuffix(currencyFormatter.format(num), suffix)
  )
}

export function formatAsShortDate({ value: x }) {
  if (!isDate(x)) return x
  const date = formatToLocalDateTime(x)
  const dateFormat =
    date.year === DateTime.local().year ? "ccc LLL d" : "ccc LLL d, y"

  return date.toFormat(dateFormat)
}

export function formatAsDate({ value: date }) {
  if (!isDate(date)) return date

  return formatToLocalDateTime(date).toISODate()
}

export function formatAsCasualDate({ value: x }) {
  if (!isDate(x)) return x

  const date = formatToLocalDateTime(x)
  const dateFormat =
    date.year === DateTime.local().year ? "cccc, LLLL d" : "cccc, LLLL d, y"

  return date.toFormat(dateFormat)
}

export const DATE_FORMATS = {
  LongMonthDayYear: "LLLL d, yyyy", // January 13, 2026 (Long month)
  ShortMonthDayYear: "LLL d, yyyy", // Jan 13, 2026 (Short month)
  ShortMonthYear: "LLL-yy", // Jan-26 (Month and year)
  MonthDayYear: "M/d/yyyy", // 1/13/2026 (US)
  MonthDayShortYear: "M/d/yy", // 1/13/26 (US, short year)
  DayMonthYear: "dd/MM/yyyy", // 13/01/2026 (European)
}

const DATE_FORMAT_DESCRIPTIONS = {
  Date: "ISO, default",
  CasualDate: "year hidden if current",
  LongMonthDayYear: "Long month",
  ShortMonthDayYear: "Short month",
  ShortMonthYear: "Month and year",
  MonthDayYear: "US",
  MonthDayShortYear: "US, short year",
  DayMonthYear: "European",
}

function formatAsDateWith(pattern) {
  return ({ value }) =>
    isDate(value) ? formatToLocalDateTime(value).toFormat(pattern) : value
}

export function dateFormatOptions() {
  const sample = DateTime.local().set({ month: 1, day: 13 }).toISODate()

  return Object.keys(DATE_FORMAT_DESCRIPTIONS).map((value) => ({
    value,
    label: `${formatAs(value, { value: sample })} (${DATE_FORMAT_DESCRIPTIONS[value]})`,
  }))
}

export function formatAsRelativeDateTime(input) {
  return DateTime.fromISO(input).toRelative()
}

export function formatAsRelativeDate({ value: x }, { compact = false } = {}) {
  if (!isDate(x)) return x

  const localDate = formatToLocalDateTime(x)

  const daysTill = daysFromToday(x)

  switch (daysTill) {
    case 0:
      return "Today"
    case 1:
      return "Tomorrow"
    case -1:
      return "Yesterday"
    default:
      if (daysTill > 2 && daysTill < 7) {
        return localDate.toFormat("cccc")
      } else {
        return `${
          compact
            ? formatAsShortDate({ value: localDate })
            : formatAsCasualDate({ value: localDate })
        }`
      }
  }
}

function formatAsBoolean({ value: x }) {
  return x ? "Yes" : "No"
}

function formatAsSentence({ value, label }) {
  const toFormat = label || value
  if (isNotAValue(toFormat)) return ""
  return humanize(toFormat)
}

function formatAsText({ value, label }) {
  return label || value
}

function formatAsWithoutFractions({ value: x, unit }) {
  if (isNotANumber(x) || isNotAValue(unit)) return x

  const { num, suffix } = { num: Number(x), suffix: "" }

  const currencyFormatter = new Intl.NumberFormat("en", {
    style: "currency",
    currencySign: "accounting",
    currency: unit,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  return stripDollarCountryCodePrefix(
    withSuffix(currencyFormatter.format(num), suffix)
  )
}

function formatAsWithFractions({ value: x, unit }) {
  if (isNotANumber(x) || isNotAValue(unit)) return x

  const { num, suffix } = { num: Number(x), suffix: "" }

  const currencyFormatter = new Intl.NumberFormat("en", {
    style: "currency",
    currencySign: "accounting",
    currency: unit,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  return stripDollarCountryCodePrefix(
    withSuffix(currencyFormatter.format(num), suffix)
  )
}

export function formatList(list) {
  if (!Array.isArray(list)) return null

  if (list.length === 1) return list[0]

  if (list.length === 2) return list.join(" and ")
  if (list.length > 2) {
    return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`
  }
}

const formatters = {
  Area: formatWithUnit,
  AbbreviatedNumber: formatAsAbbreviatedNumber,
  AbbreviatedMoney: formatAsAbbreviatedMoney,
  AbbreviatedArea: formatAsAbbreviatedWithUnit,
  AbbreviatedDistance: formatAsAbbreviatedWithUnit,
  AbbreviatedLength: formatAsAbbreviatedWithUnit,
  Boolean: formatAsBoolean,
  BigNumber: formatAsBigNumber,
  WholeNumberNumber: formatAsWholeNumber,
  WholeNumberArea: formatAsWholeNumberWithUnit,
  WholeNumberDistance: formatAsWholeNumberWithUnit,
  WholeNumberLength: formatAsWholeNumberWithUnit,
  BigMoney: formatAsBigMoney,
  CasualDate: formatAsCasualDate,
  RelativeDate: formatAsRelativeDate,
  LongMonthDayYear: formatAsDateWith(DATE_FORMATS.LongMonthDayYear),
  ShortMonthDayYear: formatAsDateWith(DATE_FORMATS.ShortMonthDayYear),
  ShortMonthYear: formatAsDateWith(DATE_FORMATS.ShortMonthYear),
  MonthDayYear: formatAsDateWith(DATE_FORMATS.MonthDayYear),
  MonthDayShortYear: formatAsDateWith(DATE_FORMATS.MonthDayShortYear),
  DayMonthYear: formatAsDateWith(DATE_FORMATS.DayMonthYear),
  Contact: formatAsText,
  Company: formatAsText,
  Portfolio: formatAsText,
  User: formatAsText,
  InternalContact: formatAsText,
  Distance: formatWithUnit,
  Money: formatAsMoney,
  Date: formatAsDate,
  NumberInWords: formatAsNumberInWords,
  Percent: formatAsPercentage,
  Sentence: formatAsSentence,
  Text: formatAsText,
  Lookup: formatAsText,
  Length: formatWithUnit,
  Size: formatAsSize,
  WithFractions: formatAsWithFractions,
  WithoutFractions: formatAsWithoutFractions,
  DecimalArea: formatAsDecimalWithUnit,
  DecimalsArea: formatAsDecimalsWithUnit,
  DecimalDistance: formatAsDecimalWithUnit,
  DecimalsDistance: formatAsDecimalsWithUnit,
  DecimalLength: formatAsDecimalWithUnit,
  DecimalsLength: formatAsDecimalsWithUnit,
  DecimalNumber: formatAsNumberDecimal,
  DecimalsNumber: formatAsNumberDecimals,
}

export function formatAs(format, input, options) {
  if (typeof input !== "object" || input === null) return input
  if (!(format in formatters)) return input.value
  return formatters[format](input, options)
}

export function formatValue(value, options) {
  if (typeof value !== "object" || value === null) return value
  const formatsArray = ["Abbreviated", "Decimal", "Decimals", "WholeNumber"]
  const { format, kind } = value

  const formatToUse =
    formatsArray.includes(format) && format && kind
      ? `${format}${kind}`
      : format || kind

  return formatAs(formatToUse, value, options)
}
